import { useState } from 'react';
import { MenuItem, Select } from '@mui/material';
import { Modal } from '@design-system/components/modal/modal';
import { Radio, RadioGroup } from '@design-system/components/radio/radio';
import { useGetAllProgramsQuery } from 'generated/graphql';
import {
  DuplicateDialogProps,
  DuplicateTarget,
  resolveTargetProgramId,
  toDuplicateTarget,
} from '../model/duplicate.domain';
import * as styles from './DuplicateWorkoutDialog.css';

type CollectionOption = { id: number; name: string };

const useOtherCollections = (
  influencerId: number,
  sourceProgramId: number,
  enabled: boolean,
): { options: CollectionOption[]; loading: boolean } => {
  const { data, loading } = useGetAllProgramsQuery({
    variables: { id: influencerId },
    skip: !enabled,
  });
  const options = (data?.creator?.programs ?? []).flatMap((program) =>
    program && program.id !== sourceProgramId
      ? [{ id: program.id, name: program.name ?? `Collection ${program.id}` }]
      : [],
  );
  return { options, loading };
};

export const DuplicateWorkoutDialog = (p: DuplicateDialogProps) => {
  const [target, setTarget] = useState<DuplicateTarget>('same');
  const [otherProgramId, setOtherProgramId] = useState<number | null>(null);
  const collections = useOtherCollections(
    p.influencerId,
    p.sourceProgramId,
    target === 'other',
  );
  const canConfirm = target !== 'other' || otherProgramId !== null;

  return (
    <Modal
      isOpen
      onClose={p.onClose}
      title="Duplicate workout"
      description="What would you like to do with the copy?"
      testId="duplicate-workout-dialog"
      primaryCta={{
        caption: 'Duplicate',
        state: canConfirm ? undefined : 'disabled',
        onClick: () =>
          p.onConfirm(
            resolveTargetProgramId(target, p.sourceProgramId, otherProgramId),
          ),
      }}
      secondaryCta={{
        caption: 'Cancel',
        secondary: true,
        onClick: p.onClose,
      }}
    >
      <div className={styles.body}>
        <RadioGroup
          value={target}
          onChange={(value) => setTarget(toDuplicateTarget(value))}
          ariaLabel="Where should the copy go"
        >
          <Radio value="same" label="Keep it in the same collection" />
          <Radio value="unassigned" label="Leave it unassigned" />
          <Radio value="other" label="Assign it to a different collection" />
        </RadioGroup>
        {target === 'other' ? (
          <Select<number | ''>
            className={styles.collectionSelect}
            displayEmpty
            value={otherProgramId ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              setOtherProgramId(typeof value === 'number' ? value : null);
            }}
            renderValue={(value) =>
              value === ''
                ? collections.loading
                  ? 'Loading...'
                  : 'Select collection'
                : collections.options.find((c) => c.id === value)?.name
            }
            inputProps={{ 'aria-label': 'Collection' }}
          >
            {collections.options.map((collection) => (
              <MenuItem key={collection.id} value={collection.id}>
                {collection.name}
              </MenuItem>
            ))}
          </Select>
        ) : null}
      </div>
    </Modal>
  );
};
